import React, { forwardRef } from 'react'

import Button from '@mui/material/Button'

type ColorPalette = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
type SizeVariant = 'small' | 'medium' | 'large'

interface CustomButtonProps {
  loading: boolean
  children: React.ReactNode
  color?: ColorPalette
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  variant?: 'contained' | 'outlined'
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  size?: SizeVariant
  tooltip?: string
  fullWidth?: boolean
  disabled?: boolean
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void
  ref?: React.Ref<HTMLButtonElement>
}

const CustomButton = forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      loading,
      children,
      color = 'primary',
      onClick,
      type = 'button',
      variant = 'contained',
      size = 'small',
      startIcon = null,
      endIcon = null,
      tooltip = '',
      fullWidth = false,
      disabled = false,
      onKeyDown = () => {},
      ...props
    },
    ref
  ) => {
    return (
      <Button
        ref={ref}
        type={type}
        variant={variant}
        size={size}
        title={tooltip}
        fullWidth={fullWidth}
        sx={{
          borderRadius: '3.5rem',
          ...(!fullWidth && {
            width: { xs: '100%', sm: 'auto' },
            minWidth: '125px',
            maxWidth: '275px'
          }),

          height: '2.2rem',
          typography: 'body2',
          padding: '0 1rem'
        }}
        disabled={loading || disabled}
        endIcon={endIcon}
        startIcon={loading ? <i className={`ri-loader-4-line ri-spin animate-spin`} /> : startIcon}
        color={color}
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-busy={loading}
        {...props}
      >
        {loading ? 'Loading' : children}
      </Button>
    )
  }
)

CustomButton.displayName = 'CustomButton'

export default CustomButton
